import { computed, isSignal, Signal, signal } from '@angular/core';
import { AppInjector } from 'src/app/app-injector.module';
import { AppContext } from 'src/app/app/context';
import { AppUserWrapper } from 'src/app/app/user';
import { CustomFieldDto, EnterpriseUserDataDto, LocaleDto, VisualDto } from 'src/app/services/api.services';
import { getTimeZoneById, TimeZone } from 'src/app/shared/utils';
import { AppCurrencies, AppCurrency } from 'src/app/state/app/app.reducer';
import { DataModel, DataSchema } from '../dataModel';
import { DataFormatter } from '../formatting/DataFormatter';
import { CustomFieldFunctionResolver } from './customFields/CustomFieldFunctionResolver';
import { CustomFields } from './customFields/CustomFields';
import { CustomFieldSyntaxVisitor } from './customFields/CustomFieldSyntaxVisitor';
import { VisualCustomField } from './VisualCustomField';
import { VisualFieldTargetResolver } from './VisualFieldTargetResolver';
import { VisualInteractions } from './VisualInteractions';
import { VisualType } from './VisualType';
import { getInsightsPbiChartType } from '../../shared/chart/chart';
import { deepClone } from '../../shared/clone/cloner-service';

export class VisualContext {
  constructor(private readonly _init: VisualContext.Init) { }

  readonly debugName = signalFrom(this._init.debugName);
  readonly inEditor = !!this._init.inEditor;

  readonly app = this._init.app;
  readonly dataModel = this._init.dataModel;

  readonly user = this._init.user;
  readonly enterpriseUserData = this._init.enterpriseUserData;
  readonly schema = this._init.schema;
  readonly interactions = this._init.interactions;

  readonly customFields = signalFrom(this._init.customFields, []);

  readonly fieldResolver = signalFrom(this._init.fieldResolver);
  readonly functionResolver = signalFrom(this._init.functionResolver);

  readonly visualFieldTargetResolver = signalFrom(
    this._init.visualFieldTargetResolver,
    () => new VisualFieldTargetResolver(this.dataModel, this.customFields));


  readonly locale = signalFrom(this._init.locale);
  readonly tz = signalFrom(this._init.tz);
  
  readonly currencies = signalFrom(this._init.currencies);
  readonly currency = signalFrom(this._init.currency);

  readonly visualData = signalFrom(this._init.visualData, {});

  readonly dataFormatter = computed(() => {
    let locale = this.locale();
    let currency = this.currency();
    let tz = this.tz();

    return DataFormatter.createDefault({
      locale,
      tz,
      currencies: this.currencies(),
      currency: currency,
      visualData: this.visualData
    });
  });


  getLocale(id: string): LocaleDto {
    if (!id)
      return null;

    id = id.toLowerCase();
    return this.app.locales().find(l => l.id.toLowerCase() == id);
  };

  getTimeZone(id: string): TimeZone {
    if (!id)
      return null;

    return getTimeZoneById(id);
  }

  getCurrency(code: string): AppCurrency {
    if (!code)
      return null;

    code = code.toLowerCase();
    return this.currencies().get(code);
  };
}

function signalFrom<T>(value: T | Signal<T>): Signal<T>;
function signalFrom<T>(value: T | Signal<T>, defaultValue: T): Signal<T>;
function signalFrom<T>(value: T | Signal<T>, factory: () => T): Signal<T>;
function signalFrom<T>(value: T | Signal<T>, defaultValueOrFactory?: any): Signal<T> {
  return isSignal(value) 
    ? value 
    : signal(value ?? (
      typeof defaultValueOrFactory === 'function' 
        ? defaultValueOrFactory()
        : defaultValueOrFactory));
}

export namespace VisualContext {
  export interface Init {
    readonly inEditor?: boolean;

    readonly app: AppContext;
    readonly user: AppUserWrapper;
    readonly enterpriseUserData: EnterpriseUserDataDto;
    readonly dataModel: DataModel;

    readonly schema: DataSchema;
    readonly interactions: VisualInteractions | null;

    readonly debugName?: 
      string |
      Signal<string>;
   
    readonly locale?: 
      Readonly<LocaleDto> | 
      Signal<Readonly<LocaleDto>>;

    readonly tz?: 
      TimeZone | 
      Signal<TimeZone>;

    readonly currencies:
      AppCurrencies |
      Signal<AppCurrencies>;

    readonly currency?: 
      AppCurrency | 
      Signal<AppCurrency>;
    
    readonly fieldResolver?: 
      CustomFieldSyntaxVisitor.FieldResolver | 
      Signal<CustomFieldSyntaxVisitor.FieldResolver>;
    
    readonly functionResolver?:
      CustomFieldSyntaxVisitor.FunctionResolver | 
      Signal<CustomFieldSyntaxVisitor.FunctionResolver>;
    
    readonly visualFieldTargetResolver?: 
      VisualFieldTargetResolver | 
      Signal<VisualFieldTargetResolver>;
   
    readonly customFields?: 
      ReadonlyArray<VisualCustomField> |
      Signal<ReadonlyArray<VisualCustomField>>;
   
    readonly visualData?: 
      DataFormatter.VisualData | 
      Signal<DataFormatter.VisualData>;
  }

  export interface DefaultWithOptions {
    readonly inEditor?: boolean;

    readonly dataModel: DataModel;

    readonly schema: DataSchema;
    readonly interactions: VisualInteractions | null;

    readonly debugName?: 
      string |
      Signal<string>;
   
    readonly locale?: 
      Readonly<LocaleDto> | 
      Signal<Readonly<LocaleDto>>;

    readonly tz?: 
      Readonly<TimeZone> | 
      Signal<Readonly<LocaleDto>>;
      
    readonly currencies:
      AppCurrencies |
      Signal<AppCurrencies>;

    readonly currency?:
      string | AppCurrency |
      Signal<string | AppCurrency | null>;
    
    readonly fieldResolver?: 
      CustomFieldSyntaxVisitor.FieldResolver | 
      Signal<CustomFieldSyntaxVisitor.FieldResolver>;
    
    readonly functionResolver?:
      CustomFieldSyntaxVisitor.FunctionResolver | 
      Signal<CustomFieldSyntaxVisitor.FunctionResolver>;
    
    readonly visualFieldTargetResolver?: 
      VisualFieldTargetResolver | 
      Signal<VisualFieldTargetResolver>;
   
    readonly customFields?: 
      ReadonlyArray<VisualCustomField> |
      ReadonlyArray<CustomFieldDto> | 
      Signal<ReadonlyArray<VisualCustomField>>;
   
    readonly visualData?: 
      DataFormatter.VisualData | 
      Signal<DataFormatter.VisualData>;
  }

  export function defaultWith(options: DefaultWithOptions): VisualContext {
    let appContext           = AppInjector.get(AppContext);
    let customFieldFunctions = AppInjector.get(CustomFieldFunctionResolver);
    
    let locale =
      isSignal(options.locale) 
        ? options.locale 
        : computed(() => typeof options.locale === 'string' ? appContext.findLocaleById(options.locale) : options.locale);
    
    let currencies =
      isSignal(options.currencies) 
        ? options.currencies 
        : computed(() => <AppCurrencies>options.currencies);
    
    let currency =
      isSignal(options.currency) 
        ? options.currency 
        : computed(() => typeof options.currency === 'string' ? currencies().get(options.currency) : options.currency);
    
    let baseFieldResolver    = options.dataModel.customFieldIdentifierLookup.resolver;
    let baseFunctionResolver = customFieldFunctions.resolver;

    let fieldResolver = computed(() => {
      let fieldResolver = isSignal(options.fieldResolver)
        ? options.fieldResolver() 
        : options.fieldResolver ?? ((identifier) => null);

      let customFields = customFieldMap();

      return fieldResolver 
        ? ((identifier) => 
            fieldResolver(identifier) 
              ?? baseFieldResolver(identifier) 
                ?? customFields.get(identifier.toString().toLowerCase()))
        : baseFieldResolver;
    });

    let functionResolver = computed(() => {
      let functionResolver = isSignal(options.functionResolver)
        ? options.functionResolver() 
        : options.functionResolver ?? ((name, args) => null);

      return functionResolver 
        ? (name, args) => functionResolver(name, args) ?? baseFunctionResolver(name, args)
        : baseFunctionResolver;
    });

    
    let customFields = computed<ReadonlyArray<VisualCustomField>>(() => {
      let context = {
        schema: options.schema,
        fieldResolver,
        functionResolver,
      };

      let fields =
        isSignal(options.customFields)
          ? options.customFields()
          : (options.customFields ?? []);

      return fields.map(f =>
        (f instanceof VisualCustomField) ? f
          : new VisualCustomField({
              context,
              name: f.name,
              sourceText: f.sourceText,
              dto: f
            }));
    });

    let customFieldMap = computed(() => {
      let map = new Map<string, CustomFields.Field>();

      for (let f of customFields())
        map.set(f.identifierExpr().toLowerCase(), f.target());

      return map;
    });


    return new VisualContext({
      debugName: options.debugName,
      inEditor: options.inEditor,

      app: appContext,

      user: appContext.user(),
      enterpriseUserData: appContext.enterpriseUserData(),

      dataModel: options.dataModel,
      schema: options.schema,

      interactions: options.interactions,

      customFields: customFields,

      fieldResolver: fieldResolver,
      functionResolver: functionResolver,
      visualFieldTargetResolver: options.visualFieldTargetResolver,

      locale: locale,
      tz: appContext.timeZone,

      currencies,
      currency,

      visualData: options.visualData
    });
  }

  export function forVisual(visualDto: VisualDto, options: {
    currencies: AppCurrencies | Signal<AppCurrencies>;
    dataModel: DataModel;
    interactions: VisualInteractions;
    locale?: LocaleDto | Signal<LocaleDto>;
  }) {
    let schema = options.dataModel.schemas.getByTarget(visualDto.schema);

    let pbiChartType = getInsightsPbiChartType(visualDto.content.type);
    let visualType = pbiChartType?.visualType;

    return defaultWith({
      debugName: visualDto.name,
      dataModel: options.dataModel,
      schema,
      interactions: options.interactions,

      currencies: options.currencies,
      currency: visualDto.content.currency,

      customFields: visualDto.content.customFields, 
      visualData: {
        visualId: visualDto.id,
        name: visualDto.name,
        type: visualType,
      },

      fieldResolver: options.dataModel.customFieldIdentifierLookup.resolver,

      locale: options.locale,
    });
  }
}
